import { useEffect, useState, useMemo, useCallback, useRef } from "react"

import {
  Box,
  Typography,
  Alert,
  Chip,
  Skeleton,
  Card,
  CardContent,
  Button,
  Tooltip,
} from '@mui/material'
import { type GridColDef, type GridRenderCellParams, type GridPaginationModel } from '@mui/x-data-grid'
import { useNavigate } from "react-router-dom"

import { handleApiError, usersService, type User, type UsersStats, UserType } from "../api"
import { AdminDataGrid } from "../components/admin-data-grid"
import { LinkCell, DateCell, StatusChip } from "../components/data-grid-cells"
import { formatDateTime } from "../components/data-grid-utils"
import { AuthCodeDialog } from "../components/users/AuthCodeDialog"
import { useUser } from "../hooks/use-user"
import { isAdminUser } from "../lib/user-utils"

type UsersListPageProps = {
  userType: UserType
  title: string
  description: string
  csvPrefix: string
}

export default function UsersListPage({ userType, title, description, csvPrefix }: UsersListPageProps) {
  const [users, setUsers] = useState<User[]>([])
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState<UsersStats>({ verified: 0, purchased: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState("")
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 50 })
  const pageSizeRef = useRef(50)
  const fetchIdRef = useRef(0)
  const { user: currentUser } = useUser()
  const navigate = useNavigate()

  const isAdmin = isAdminUser(currentUser)

  const fetchUsers = useCallback(async (page: number, pageSize: number) => {
    const id = ++fetchIdRef.current
    try {
      setIsLoading(true)
      setError("")
      const data = await usersService.getUsers({
        role: "user",
        userType,
        page: page + 1,
        size: pageSize,
      })
      if (fetchIdRef.current !== id) return
      setUsers(data.users)
      setTotal(data.total)
      setStats(data.stats)
    } catch (err) {
      if (fetchIdRef.current !== id) return
      setError(handleApiError(err))
    } finally {
      if (fetchIdRef.current === id) setIsLoading(false)
    }
  }, [userType])

  useEffect(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }))
    fetchUsers(0, pageSizeRef.current)
  }, [fetchUsers])

  const handlePaginationChange = useCallback((model: GridPaginationModel) => {
    pageSizeRef.current = model.pageSize
    setPaginationModel(model)
    fetchUsers(model.page, model.pageSize)
  }, [fetchUsers])

  const csvEscape = (value: unknown): string => {
    const str = value == null ? '' : String(value)
    const needsQuoting = /[",\n]/.test(str)
    const escaped = str.replace(/"/g, '""')
    return needsQuoting ? `"${escaped}"` : escaped
  }

  const handleExportCsv = async () => {
    try {
      setIsExporting(true)
      const allUsers = await usersService.getAllUsers("user", userType)

      const headers = [
        'Email',
        'First Name',
        'Last Name',
        'Status',
        'Created',
      ]

      const rows = allUsers.map(u => [
        csvEscape(u.email),
        csvEscape(u.firstName || ''),
        csvEscape(u.lastName || ''),
        csvEscape(u.isVerified ? 'Verified' : 'Unverified'),
        csvEscape(formatDateTime(u.createdAt)),
      ].join(','))

      const bom = '\ufeff'
      const csv = [headers.join(','), ...rows].join('\n')
      const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const dateStamp = new Date().toISOString().split('T')[0]
      a.href = url
      a.download = `${csvPrefix}-${dateStamp}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsExporting(false)
    }
  }

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <LinkCell to={`/users/${params.row.id}`}>
          {params.value}
        </LinkCell>
      ),
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'isVerified',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <StatusChip
          label={params.value ? 'Verified' : 'Unverified'}
          color={params.value ? 'success' : 'warning'}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => (
        <DateCell value={params.value as string} />
      ),
    },
    ...(isAdmin ? [{
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <AuthCodeDialog user={params.row as User} />
      ),
    }] : []),
  ], [isAdmin])

  if (isLoading && users.length === 0) {
    return (
      <Box sx={{ flex: 1, p: 3 }}>
        <Card sx={{ height: 'fit-content' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width={300} height={40} />
            </Box>
            <Box sx={{ height: 'calc(100vh - 300px)' }}>
              <Skeleton variant="rectangular" width="100%" height="100%" />
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Card sx={{ height: 'fit-content' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold">
                {title} ({total} total)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {description}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={handleExportCsv} disabled={total === 0 || isExporting}>
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <Tooltip title="Users who entered the email verification code">
              <Chip
                label={`Verified: ${stats.verified}`}
                color="success"
                size="small"
                variant="outlined"
              />
            </Tooltip>
            <Chip
              label={`Unverified: ${total - stats.verified}`}
              color="warning"
              size="small"
              variant="outlined"
            />
            <Tooltip title="Users who have placed at least one order">
              <Chip
                label={`Purchased: ${stats.purchased}`}
                color="info"
                size="small"
                variant="outlined"
              />
            </Tooltip>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <AdminDataGrid
                rows={users}
                columns={columns}
                rowCount={total}
                loading={isLoading}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationChange}
                rowHeight={52}
                pageSizeOptions={[25, 50, 100, 200]}
                disableColumnMenu={false}
                disableColumnFilter={false}
                disableColumnSelector={false}
                disableDensitySelector={false}
                density="compact"
                onRowClick={(params) => {
                  navigate(`/users/${params.id}`)
                }}
                sx={{
                  height: 'calc(100vh - 300px)',
                  '& .MuiDataGrid-columnHeaders': { fontWeight: 600 },
                }}
              />
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </Box>
  )
}
